import { useState } from "react";
import axios from "axios";
import { ChevronLeft, PenLine, Loader2, CheckCircle2, FileText, MessageCircle } from "lucide-react";
import {
  API,
  FOCUS,
  entetes,
  estNonAutorise,
  messageErreur,
  nomComplet,
  numeroWhatsapp,
  entrerPleinEcranPaysage,
} from "./outils";
import SignaturePleinEcran from "./SignaturePleinEcran";

const ETAPES = ["1. Lire", "2. Signer", "3. Envoyer"];

export default function EcranConsentement({ code, personne: p, signatureOuverte, onOuvrirSignature, onFermerSignature, onRetour, onLogout }) {
  const [langue, setLangue] = useState("fr");
  const [signature, setSignature] = useState("");
  const [accordWhatsapp, setAccordWhatsapp] = useState(false);
  const [etat, setEtat] = useState("idle"); // idle | envoi | ok | erreur
  const [resultat, setResultat] = useState(null);
  const [messageErr, setMessageErr] = useState("");
  const femme = (p.sexe || "").toUpperCase().startsWith("F");
  const civ = femme ? "Mme" : "M.";
  const civAr = femme ? "السيدة" : "السيد";
  const ou = (v) => v || "…………";
  const nom = nomComplet(p);

  // Plein écran + paysage demandés DANS le clic (geste utilisateur exigé par le navigateur)
  const ouvrirSignature = () => {
    entrerPleinEcranPaysage();
    onOuvrirSignature();
  };

  const valider = async () => {
    if (!signature || etat === "envoi") return;
    setEtat("envoi");
    setMessageErr("");
    try {
      const { data } = await axios.post(
        `${API}/mobile/consentement`,
        { demande_id: p.id, signature_base64: signature, accord_whatsapp: accordWhatsapp },
        { headers: entetes(code), timeout: 90000 }
      );
      setResultat(data);
      setEtat("ok");
    } catch (err) {
      if (estNonAutorise(err)) {
        onLogout();
        return;
      }
      setMessageErr(messageErreur(err, "Envoi impossible. Réessayez."));
      setEtat("erreur");
    }
  };

  const etapeFaite = [true, !!signature, etat === "ok"];

  return (
    <div className="ecran flex flex-col bg-[#F4F6F9] text-[#0F172A]" data-testid="ecran-consentement">
      <header className="flex-none sticky top-0 z-10 bg-[#0F172A] px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))] flex items-center gap-2">
        <button
          type="button"
          onClick={onRetour}
          aria-label="Retour à la fiche"
          data-testid="btn-fermer-consentement"
          className={`w-11 h-11 -ml-2.5 flex-none flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}
        >
          <ChevronLeft aria-hidden="true" className="w-[22px] h-[22px]" />
        </button>
        <div className="flex flex-col min-w-0">
          <h1 className="m-0 text-white text-base font-bold">Consentement client</h1>
          <span className="text-[#94A3B8] text-xs truncate">{[nom, p.ref_capago].filter(Boolean).join(" · ")}</span>
        </div>
      </header>

      <ol aria-label="Étapes" className="flex-none list-none m-0 px-4 pt-3 grid grid-cols-3 gap-1.5">
        {ETAPES.map((libelle, i) => (
          <li key={libelle} className="flex flex-col gap-1.5" aria-current={!etapeFaite[i] && etapeFaite[i - 1] !== false ? "step" : undefined}>
            <span className={`h-1 rounded-sm ${etapeFaite[i] ? "bg-[#0F766E]" : "bg-[#CBD5E1]"}`} />
            <span className={`text-xs ${etapeFaite[i] ? "font-bold text-[#0F766E]" : "font-semibold text-[#475569]"}`}>{libelle}</span>
          </li>
        ))}
      </ol>

      {etat === "ok" && resultat ? (
        <main className="flex-1 min-h-0 overflow-y-auto p-3">
          <section
            className="bg-white border border-[#A7F3D0] rounded-[14px] p-5 flex flex-col gap-3"
            data-testid="consentement-ok"
            role="status"
          >
            <div className="flex items-center gap-2 text-[#065F46] font-bold text-base">
              <CheckCircle2 aria-hidden="true" className="w-6 h-6" />
              Consentement signé et archivé
            </div>
            <p className="m-0 text-sm text-[#334155]">
              {resultat.email_envoye
                ? `Le PDF (FR + AR) a été envoyé par e-mail à : ${(resultat.destinataires || []).join(", ")}.`
                : "PDF archivé. L'e-mail n'a pas pu être envoyé."}
            </p>
            <a
              href={resultat.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lien-pdf-consentement"
              className={`h-[52px] flex items-center justify-center gap-2 bg-[#0F766E] text-white text-base font-bold rounded-xl ${FOCUS}`}
            >
              <FileText aria-hidden="true" className="w-5 h-5" /> Ouvrir le PDF
            </a>
            {p.telephone ? (
              <a
                href={`https://wa.me/${numeroWhatsapp(p.telephone)}?text=${encodeURIComponent(
                  `Bonjour ${civ} ${nom}, voici votre déclaration de consentement signée (Khaldoun Office) : ${resultat.url}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-whatsapp-consentement"
                className={`h-[52px] flex items-center justify-center gap-2 bg-[#047857] text-white text-base font-bold rounded-xl ${FOCUS}`}
              >
                <MessageCircle aria-hidden="true" className="w-5 h-5" /> Envoyer au client par WhatsApp
              </a>
            ) : null}
            <button
              type="button"
              onClick={onRetour}
              className={`h-11 rounded-xl border border-[#CBD5E1] bg-white text-[#334155] text-sm font-semibold ${FOCUS}`}
            >
              Retour
            </button>
          </section>
        </main>
      ) : (
        <>
          <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-3 p-3 [&>*]:shrink-0">
            <div role="group" aria-label="Langue du texte" className="grid grid-cols-2 bg-[#E2E8F0] rounded-[10px] p-[3px]">
              {[
                ["fr", "Français"],
                ["ar", "العربية"],
              ].map(([cle, libelle]) => (
                <button
                  key={cle}
                  type="button"
                  lang={cle}
                  aria-pressed={langue === cle}
                  onClick={() => setLangue(cle)}
                  className={`h-11 rounded-lg text-sm ${FOCUS} ${
                    langue === cle ? "bg-white text-[#0F172A] font-bold shadow-sm" : "bg-transparent text-[#334155] font-semibold"
                  }`}
                >
                  {libelle}
                </button>
              ))}
            </div>

            <article
              data-testid="texte-consentement"
              className="bg-white border border-[#E2E8F0] rounded-[14px] p-4 flex flex-col gap-2.5 text-sm leading-[1.55]"
            >
              {langue === "fr" ? (
                <>
                  <h2 className="m-0 text-center text-[15px] font-bold">DÉCLARATION DE CONSENTEMENT</h2>
                  <p className="m-0">
                    {femme ? "Je soussignée" : "Je soussigné"}{" "}
                    <strong>
                      {civ} {nom}
                    </strong>
                    , {femme ? "née" : "né"} le <strong>{ou(p.date_naissance)}</strong> à <strong>{ou(p.lieu_naissance)}</strong>,
                    titulaire du passeport n° <strong>{ou(p.passeport)}</strong>, téléphone <strong>{ou(p.telephone)}</strong>, déclare
                    avoir déposé mon passeport auprès de <strong>Khaldoun Office</strong> afin d'effectuer la prise de rendez-vous pour
                    ma demande de visa.
                  </p>
                  <p className="m-0 font-semibold">
                    Je reconnais et j'accepte que toute annulation de ma part avant la fin de la procédure entraîne le paiement
                    d'une somme de <strong className="whitespace-nowrap">2 000 DA</strong> (deux mille dinars algériens).
                  </p>
                  <p className="m-0 font-semibold bg-[#FFF1F3] border-l-4 border-[#E11D48] rounded-lg px-3 py-2.5" data-testid="clause-totalite">
                    Si un rendez-vous a été obtenu pour mon dossier et que j'annule ma demande de mon propre fait, je reste
                    redevable de la totalité des frais convenus avec le bureau Khaldoun Office.
                  </p>
                </>
              ) : (
                <div dir="rtl" lang="ar" className="flex flex-col gap-2.5 text-right text-[15px] leading-[1.8]">
                  <h2 className="m-0 text-center text-base font-bold">تصريح بالموافقة</h2>
                  <p className="m-0">
                    {femme ? "أنا الموقّعة أدناه" : "أنا الموقّع أدناه"}{" "}
                    <strong>
                      {civAr} <bdi>{nom}</bdi>
                    </strong>
                    ، {femme ? "المولودة" : "المولود"} بتاريخ{" "}
                    <strong>
                      <bdi dir="ltr">{ou(p.date_naissance)}</bdi>
                    </strong>{" "}
                    بـ<strong><bdi>{ou(p.lieu_naissance)}</bdi></strong>، {femme ? "حاملة" : "حامل"} جواز السفر رقم{" "}
                    <strong>
                      <bdi dir="ltr" className="whitespace-nowrap">{ou(p.passeport)}</bdi>
                    </strong>
                    ، رقم الهاتف{" "}
                    <strong>
                      <bdi dir="ltr" className="whitespace-nowrap">{ou(p.telephone)}</bdi>
                    </strong>
                    ، أصرّح بأنني أودعت جواز سفري لدى <bdi className="whitespace-nowrap">Khaldoun Office</bdi> من أجل أخذ موعد لطلب التأشيرة.
                  </p>
                  <p className="m-0 font-semibold">
                    وأوافق على أن أي إلغاء من طرفي قبل نهاية الإجراءات يترتب عليه دفع مبلغ{" "}
                    <strong className="whitespace-nowrap"><bdi dir="ltr">2000</bdi> دج</strong> (ألفا دينار جزائري).
                  </p>
                  <p className="m-0 font-semibold bg-[#FFF1F3] border-r-4 border-[#E11D48] rounded-lg px-3 py-2.5">
                    في حال الحصول على موعد لملفي وقمت بإلغاء طلبي بمبادرة مني، أبقى ملزمًا بدفع كامل المصاريف المتفق عليها مع مكتب خلدون.
                  </p>
                </div>
              )}
              <p className="m-0 text-xs text-[#475569]">
                Une seule signature vaut pour la version française et la version arabe.
              </p>
            </article>

            <label
              className="flex items-start gap-3 bg-white border border-[#E2E8F0] rounded-[14px] p-3.5 text-sm leading-[1.5] text-[#0F172A] cursor-pointer"
              data-testid="case-accord-whatsapp"
            >
              <input
                type="checkbox"
                checked={accordWhatsapp}
                onChange={(e) => setAccordWhatsapp(e.target.checked)}
                className="mt-0.5 w-5 h-5 flex-none accent-[#0F766E]"
              />
              <span>
                J'accepte que le bureau Khaldoun Office m'informe par WhatsApp de l'avancement de mon dossier (attribution et rappel
                de mon rendez-vous).
                <span dir="rtl" lang="ar" className="block mt-1 text-right text-[#334155]">
                  أوافق على أن يُعلمني مكتب خلدون عبر واتساب بمستجدات ملفي (تحديد موعدي والتذكير به).
                </span>
              </span>
            </label>

            <section className="flex flex-col gap-2">
              <h2 className="m-0 text-[13px] font-bold text-[#334155]">Signature du client</h2>
              {signature ? (
                <div className="h-24 rounded-[14px] bg-white border border-[#E2E8F0] flex items-center justify-between px-3.5 gap-2.5">
                  <img
                    src={signature}
                    alt="Aperçu de la signature validée"
                    data-testid="apercu-signature"
                    className="h-[72px] max-w-[70%] object-contain"
                  />
                  <button
                    type="button"
                    onClick={ouvrirSignature}
                    data-testid="btn-signer"
                    className={`flex-none h-11 px-3 border border-[#CBD5E1] rounded-lg text-[#334155] text-[13px] font-semibold bg-white ${FOCUS}`}
                  >
                    Refaire
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={ouvrirSignature}
                    data-testid="btn-signer"
                    className={`min-h-[64px] rounded-[14px] bg-[#0F766E] text-white flex items-center justify-center gap-2.5 text-base font-bold ${FOCUS}`}
                  >
                    <PenLine aria-hidden="true" className="w-[22px] h-[22px]" />
                    Faire signer le client
                  </button>
                  <span className="text-xs text-[#475569] text-center">
                    S'ouvre en plein écran et passe en paysage pour signer confortablement
                  </span>
                </>
              )}
            </section>

            {etat === "erreur" ? (
              <p role="alert" className="m-0 text-[#B91C1C] text-sm bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl py-2 px-3" data-testid="consentement-erreur">
                {messageErr}
              </p>
            ) : null}
          </main>

          <div className="flex-none px-3 pt-3 pb-[max(16px,env(safe-area-inset-bottom))] bg-white border-t border-[#E2E8F0] flex flex-col gap-1.5">
            <button
              type="button"
              onClick={valider}
              disabled={!signature || etat === "envoi"}
              data-testid="btn-valider-consentement"
              className={`h-[52px] rounded-xl bg-[#0F766E] text-white text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50 ${FOCUS}`}
            >
              {etat === "envoi" ? (
                <>
                  <Loader2 aria-hidden="true" className="w-5 h-5 animate-spin" /> Génération du PDF…
                </>
              ) : (
                "Valider et envoyer par e-mail"
              )}
            </button>
            <span className="text-center text-xs text-[#475569]">PDF français + arabe archivé · copie au bureau</span>
          </div>
        </>
      )}

      {signatureOuverte ? (
        <SignaturePleinEcran
          nom={nom}
          onAnnuler={onFermerSignature}
          onValider={(png) => {
            setSignature(png);
            if (etat === "erreur") setEtat("idle");
            onFermerSignature();
          }}
        />
      ) : null}
    </div>
  );
}
